import { Button, Center, Stack, Text, Title } from '@mantine/core';
import type { ComponentType } from 'react';
import { createBrowserRouter, Link, Navigate } from 'react-router';
import { useAuth } from './auth/AuthProvider';
import { Loading } from './components/ui';
import { homePath } from './layout/nav';
import { Shell } from './layout/Shell';
import { AcceptInvitePage, ForgotPasswordPage, LoginPage, ResetPasswordPage } from './pages/auth/AuthPages';

function Home() {
  const { user } = useAuth();
  return <Navigate to={user ? homePath(user.permissions) : '/login'} replace />;
}

function NotFound() {
  return (
    <Center py={80}>
      <Stack align="center" gap="xs">
        <Title order={3}>Nothing here</Title>
        <Text c="dimmed">This page doesn't exist, or your role can't open it.</Text>
        <Button component={Link} to="/" variant="light" mt="sm">
          Go to your start page
        </Button>
      </Stack>
    </Center>
  );
}

/**
 * Each screen is its own file, loaded when first opened — sign-in and the shell stay small, and
 * the story editor and charts come only to those who use them.
 */
const screen =
  <M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) =>
  async () => ({ Component: (await load())[name] as ComponentType });

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  {
    element: <Shell />,
    hydrateFallbackElement: <Loading />,
    children: [
      { index: true, element: <Home /> },
      { path: 'dashboard', lazy: screen(() => import('./pages/DashboardPage'), 'DashboardPage') },
      { path: 'analytics', lazy: screen(() => import('./pages/AnalyticsPage'), 'AnalyticsPage') },
      { path: 'articles', lazy: screen(() => import('./pages/articles/ArticlesPages'), 'ArticlesListPage') },
      { path: 'articles/:id', lazy: screen(() => import('./pages/articles/ArticlesPages'), 'ArticleEditorPage') },
      { path: 'breaking', lazy: screen(() => import('./pages/BreakingPage'), 'BreakingPage') },
      { path: 'videos', lazy: screen(() => import('./pages/VideosPage'), 'VideosPage') },
      { path: 'media', lazy: screen(() => import('./pages/MediaPage'), 'MediaPage') },
      { path: 'taxonomy', lazy: screen(() => import('./pages/TaxonomyPage'), 'TaxonomyPage') },
      { path: 'notifications', lazy: screen(() => import('./pages/NotificationsPage'), 'NotificationsPage') },
      { path: 'ads', lazy: screen(() => import('./pages/AdsPage'), 'AdsPage') },
      { path: 'inbox', lazy: screen(() => import('./pages/InboxPage'), 'InboxPage') },
      { path: 'directory', lazy: screen(() => import('./pages/DirectoryPage'), 'DirectoryPage') },
      { path: 'helplines', lazy: screen(() => import('./pages/HelplinesPage'), 'HelplinesPage') },
      { path: 'transport', lazy: screen(() => import('./pages/TransportPage'), 'TransportPage') },
      { path: 'jobs', lazy: screen(() => import('./pages/KnowledgePages'), 'JobsPage') },
      { path: 'schemes', lazy: screen(() => import('./pages/KnowledgePages'), 'SchemesPage') },
      { path: 'rights', lazy: screen(() => import('./pages/KnowledgePages'), 'RightsPage') },
      { path: 'current-affairs', lazy: screen(() => import('./pages/KnowledgePages'), 'CurrentAffairsPage') },
      { path: 'menu-pages', lazy: screen(() => import('./pages/MenuPagesPage'), 'MenuPagesPage') },
      { path: 'staff', lazy: screen(() => import('./pages/AdminPages'), 'StaffPage') },
      { path: 'roles', lazy: screen(() => import('./pages/AdminPages'), 'RolesPage') },
      { path: 'pages', lazy: screen(() => import('./pages/AdminPages'), 'PagesPage') },
      { path: 'settings', lazy: screen(() => import('./pages/AdminPages'), 'SettingsPage') },
      { path: 'places', lazy: screen(() => import('./pages/StatesPanel'), 'PlacesPage') },
      { path: 'devices', lazy: screen(() => import('./pages/AdminPages'), 'DevicesPage') },
      { path: 'audit', lazy: screen(() => import('./pages/AdminPages'), 'AuditPage') },
      { path: 'account', lazy: screen(() => import('./pages/AdminPages'), 'AccountPage') },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
